/**
 * Tiny typed fetch wrapper used by every API call.
 *
 * Why not just `fetch`?
 *   - Always sends credentials (the session cookie is HttpOnly + same-origin).
 *   - Always sets Accept + Content-Type when there's a body.
 *   - Throws an `ApiError` so callers can branch on `.status` / `.body`.
 *   - Returns parsed JSON typed by the caller's generic.
 *
 * Auth lives at /auth/* (auth-svc).
 * Everything else lives at /api/v2/* (admin-bff).
 */

export class ApiError extends Error {
    status: number;
    body: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
    body?: unknown;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const init: RequestInit = {
        credentials: "include",
        headers: {
            Accept: "application/json",
            ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
            ...(opts.headers ?? {}),
        },
        ...opts,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    };

    const res = await fetch(path, init);
    const contentType = res.headers.get("content-type") ?? "";
    let parsed: unknown = null;
    if (contentType.includes("application/json")) {
        parsed = await res.json().catch(() => null);
    } else if (res.status !== 204) {
        parsed = await res.text().catch(() => null);
    }

    if (!res.ok) {
        const message =
            (parsed && typeof parsed === "object" && "error" in parsed && typeof parsed.error === "string"
                ? parsed.error
                : null) ?? `HTTP ${res.status}`;
        throw new ApiError(message, res.status, parsed);
    }

    return parsed as T;
}

export const api = {
    get:    <T>(path: string)            => request<T>(path, { method: "GET" }),
    post:   <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
    put:    <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
    patch:  <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
    delete: <T>(path: string)            => request<T>(path, { method: "DELETE" }),
};
